import { readStringList } from "./contentDecoders";
import { requestJson } from "../http/httpClient";
import { readArray, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { readAnnotations } from "../knowledgeData/lensDecoders";
import type { AnnotationSet } from "../threads/liveMessages";

/**
 * A document's content annotation sets (F84 D-02): the `dmw-annotation-set/1` artifacts on
 * any version of it, merged on `main`, then proposed in open change sets (module B).
 */
export interface DocumentAnnotationSet {
  /** The artifact version, `<item id>@<blob id>`. */
  readonly reference: string;
  readonly name: string;
  readonly state: "merged" | "proposed";
  /** For a proposed set: the change set that proposes it. */
  readonly changeSetId: string | null;
  /** For a proposed set: the Thread entries (`<thread>#<entry>`) it was saved from. */
  readonly origin: readonly string[];
  readonly annotationSet: AnnotationSet;
}

const STATES = ["merged", "proposed"] as const;

export function listDocumentAnnotationSets(projectId: string, documentId: string, signal?: AbortSignal): Promise<DocumentAnnotationSet[]> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/content/documents/${encodeURIComponent(documentId)}/annotation-sets`,
    signal,
    decodeResponse: (body) => readArray(readObject(body, "annotation sets")["items"], "items", (value) => {
      const item = readObject(value, "annotation set");
      return {
        reference: readString(item, "reference"),
        name: readString(item, "name"),
        state: readOneOf(item, "state", STATES),
        changeSetId: readNullableString(item, "change_set_id"),
        origin: readStringList(item, "origin"),
        annotationSet: readAnnotationSetContent(item["content"]),
      };
    }),
  });
}

/** The `dmw-annotation-set/1` schema of a content annotation set artifact. */
export const ANNOTATION_SET_SCHEMA = "dmw-annotation-set/1";

/** A `dmw-annotation-set/1` artifact's content as the span view takes it; throws on another shape. */
export function readAnnotationSetContent(value: unknown): AnnotationSet {
  const content = readObject(value, "annotation set content");
  const documents = Array.isArray(content["documents"]) ? readStringList(content, "documents") : [];
  return {
    output_kind: "annotations",
    document: readString(content, "document"),
    ...(documents.length === 0 ? {} : { documents }),
    label_list: readString(content, "label_list"),
    annotations: readAnnotations(content),
  };
}

/** The artifact content an edited set is saved as: the set without the Thread's `output_kind`. */
export function annotationSetContent(set: AnnotationSet): JsonObject {
  const { output_kind: _outputKind, ...content } = set;
  return content;
}
